/**
 * Masking helpers for the reserve amount inputs.
 *
 * PrimeVue InputNumber reformats its value on every keystroke and mis-restores
 * the caret — typing in the middle of a grouped number transposes digits
 * ("85" → "58") and deleting jumps the caret to the start (into the currency
 * addon). These helpers drive a plain controlled input instead: thousands
 * separators are kept while typing, and the caret is restored by digit position
 * (commas are the only thing formatting shifts), so it stays put.
 *
 * The submitted value stays a number — the input keeps a numeric model ref and
 * `parseAmount` converts the display string back, so the Workato request body is
 * unchanged (e.g. `{ max_amount: 70000 }`, never the "70,000" string).
 */

// Grouped display string: commas on the integer part, at most 2 decimals.
// Everything except digits and a single decimal point is dropped.
export function formatAmount(raw: string): string {
  let cleaned = raw.replace(/[^\d.]/g, '')
  const dot = cleaned.indexOf('.')
  if (dot !== -1) {
    // Keep only the first dot and at most two decimal digits.
    cleaned =
      cleaned.slice(0, dot + 1) +
      cleaned
        .slice(dot + 1)
        .replace(/\./g, '')
        .slice(0, 2)
  }
  const [intRaw, decPart] = cleaned.split('.')
  const intPart = intRaw.replace(/^0+(?=\d)/, '')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decPart === undefined ? grouped : `${grouped}.${decPart}`
}

// Display string → number, or null when empty / not yet a number.
export function parseAmount(text: string): number | null {
  const digits = text.replace(/,/g, '')
  if (digits === '' || digits === '.') return null
  const n = Number(digits)
  return Number.isFinite(n) ? n : null
}

// Where to put the caret after reformatting: keep it after the same count of
// digit/decimal characters it followed before. Commas are ignored because they
// are the only characters the reformat inserts or removes.
export function restoreCaret(
  oldValue: string,
  oldCaret: number,
  formatted: string,
): number {
  const target = (oldValue.slice(0, oldCaret).match(/[\d.]/g) || []).length
  if (target === 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/[\d.]/.test(formatted[i])) {
      seen++
      if (seen === target) return i + 1
    }
  }
  return formatted.length
}
